import View16 from '@carbon/icons-react/es/view/16';
import { FormInstance } from 'antd';
import React, { FC, useEffect, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { Field, Field as FieldT } from 'api/tasks';
import { Button, ButtonGroup } from 'components/atoms/button';
import { Form } from 'components/atoms/form';
import { FieldDataType } from 'components/organisms/module-settings/fields/types';
import {
  FieldType,
  filterableFieldType,
} from 'components/organisms/tasks/fields';
import {
  Drawer,
  DrawerContent,
} from 'components/organisms/timesheets/common/drawer';
import { getField } from 'routes/projects/create/project-fields-step';
import { isSelectTypeField } from 'utils/project-fields/use-project-fields';

const Footer: FC<{
  loading: boolean;
  onClear: VoidFunction;
  onSubmit?: VoidFunction;
}> = ({ loading, onClear, onSubmit }) => {
  return (
    <ButtonGroup $reversed>
      <Button
        type="primary"
        onClick={onSubmit}
        icon={<View16 />}
        iconPlacement="right"
        loading={loading}
      >
        <FormattedMessage id="templates.dynamic.conditions.preview.change.drawer.submit" />
      </Button>
      <Button onClick={onClear}>
        <FormattedMessage id="clear" />
      </Button>
    </ButtonGroup>
  );
};

const isMultiSelect = (field: Field) => {
  const type = filterableFieldType(field);

  return [FieldType.MultiUser, FieldType.MultiSelect].includes(type);
};

const getSelectType = (options: any[] = []) => {
  if (options.some((o) => 'imageUrl' in o)) {
    return 'user';
  }
  if (options.some((o) => o.color)) {
    return 'tag';
  }

  return undefined;
};

export const ChangePreviewDrawer: FC<{
  form: FormInstance;
  fields: any[];
  visible: boolean;
  onSubmit: VoidFunction;
  onClose: VoidFunction;
}> = ({ form, fields, visible, onSubmit, onClose }) => {
  const [loading, setLoading] = useState(false);

  const onClear = () => form.resetFields();
  const _onSubmit = () => {
    setLoading(true);
    onSubmit();
  };

  useEffect(() => {
    if (visible) {
      setLoading(false);
    }
  }, [visible]);

  return (
    <Drawer visible={visible} onClose={onClose}>
      <DrawerContent>
        <DrawerContent.Title>
          <FormattedMessage id="templates.dynamic.conditions.preview.change.drawer.title" />
        </DrawerContent.Title>
        <DrawerContent.Content>
          <Form form={form} layout="vertical">
            {fields
              .map((field): [FieldT, any] => [field, getField(field)])
              .map(([field, FieldComponent]) => (
                <Form.Item
                  key={field.fieldId}
                  name={[
                    'fields',
                    (field.fieldId < 0
                      ? field.fieldName
                      : field.fieldId
                    ).toString(),
                  ]}
                  label={field.fieldLabel}
                  valuePropName={
                    field.fieldDataType === FieldDataType.Boolean
                      ? 'checked'
                      : undefined
                  }
                  initialValue={
                    field.fieldDataType === FieldDataType.Boolean
                      ? false
                      : undefined
                  }
                >
                  {isSelectTypeField(field) ? (
                    <FieldComponent
                      isClearable
                      options={field.metaData?.options}
                      isMulti={isMultiSelect(field)}
                      type={getSelectType(field.metaData?.options)}
                    />
                  ) : (
                    <FieldComponent />
                  )}
                </Form.Item>
              ))}
          </Form>
        </DrawerContent.Content>
        <DrawerContent.Footer>
          <Footer loading={loading} onSubmit={_onSubmit} onClear={onClear} />
        </DrawerContent.Footer>
      </DrawerContent>
    </Drawer>
  );
};
